import { ErrorTypes, createErrorWithType, logError } from '../errorHandling';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

// Deobfuscation utilities
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

const fromBase64 = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return textDecoder.decode(bytes);
};

const deobfuscateData = (data) => {
  try {
    const OBFUSCATION_KEY = 'cap-test-key';
    const text = fromBase64(data);
    let result = '';
    for (let i = 0; i < text.length; i++) {
      result += String.fromCharCode(text.charCodeAt(i) ^ OBFUSCATION_KEY.charCodeAt(i % OBFUSCATION_KEY.length));
    }
    return JSON.parse(result);
  } catch (error) {
    console.error('Error deobfuscating data:', error);
    throw new Error('Error al procesar los datos recibidos');
  }
};

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

export const QuestionFetcher = {
  async fromApi(endpoint, token, fingerprint) {
    const headers = {
      'Content-Type': 'application/json'
    };

    // Add fingerprint for trial access if available
    if (fingerprint && typeof fingerprint === 'string' && fingerprint.length > 0) {
      headers['X-Browser-Fingerprint'] = fingerprint;
      try {
        console.log('Setting fingerprint header:', fingerprint.substring(0, 10) + '...');
        console.log('Fingerprint details:', {
          length: fingerprint.length,
          type: typeof fingerprint,
          isValid: fingerprint.length > 10
        });
      } catch (e) {
        console.log('Setting fingerprint header (could not get substring):', fingerprint);
        console.log('Fingerprint error:', e.message);
      }
    } else {
      console.log('No fingerprint available for API request:', {
        hasFingerprint: !!fingerprint,
        fingerprintType: typeof fingerprint
      });
    }

    // Add token if available
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      console.log('Setting token header: Bearer ' + token.substring(0, 10) + '...');
    } else {
      console.log('No token available, relying on trial access');
    }

    let lastError;
    for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
      try {
        if (attempt > 0) {
          console.log(`Retry attempt ${attempt + 1} of ${MAX_RETRIES}`);
          await sleep(RETRY_DELAY * attempt); // Exponential backoff
        }

        console.log('Making API request to:', endpoint);
        console.log('With headers:', JSON.stringify(headers));
        
        const response = await fetch(endpoint, {
          method: 'GET',
          headers: headers,
          credentials: 'same-origin'
        });

        if (!response.ok) {
          if (response.status === 401) {
            throw createErrorWithType(
              ErrorTypes.AUTH,
              'No tienes acceso a las preguntas. Necesitas un token válido o acceso de prueba.'
            );
          }
          if (response.status === 503) {
            // For 503, we'll retry
            throw createErrorWithType(
              ErrorTypes.SERVER_ERROR,
              'El servidor no está disponible temporalmente. Reintentando...'
            );
          }
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `Error del servidor (${response.status})`);
        }

        const data = await response.json();
        if (!data.success) {
          throw new Error(data.message || 'Error al cargar las preguntas');
        }

        // Log raw response details
        console.log('API response details:', {
          success: data.success,
          hasTests: !!data.tests,
          metadata: data.metadata,
          trial: data.trial
        });

        // If trial access info is included, log it
        if (data.trial) {
          console.log('Trial access expires at:', data.trial.expiresAt);
        }

        try {
          // Deobfuscate tests data
          const tests = deobfuscateData(data.tests);
          
          console.log('Deobfuscated tests:', {
            isArray: Array.isArray(tests),
            length: tests?.length,
            sample: tests?.[0]
          });

          // Ensure we're returning an array
          if (!Array.isArray(tests)) {
            console.error('Deobfuscated tests is not an array:', tests);
            throw new Error('Invalid response format: deobfuscated tests is not an array');
          }

          return tests;
        } catch (error) {
          console.error('Error processing tests data:', error);
          throw new Error('Error al procesar los datos de las preguntas');
        }

      } catch (error) {
        lastError = error;
        
        // If it's not a 503 error or it's the last attempt, don't retry
        if (error.type !== ErrorTypes.SERVER_ERROR || attempt === MAX_RETRIES - 1) {
          if (error.type) throw error; // Re-throw typed errors
          throw createErrorWithType(
            ErrorTypes.DATA_LOADING,
            error.message || 'Error al cargar las preguntas'
          );
        }
        
        console.log(`Request failed, retrying... (${attempt + 1}/${MAX_RETRIES})`);
      }
    }

    // If we get here, all retries failed
    throw createErrorWithType(
      ErrorTypes.SERVER_ERROR,
      'El servidor no está disponible temporalmente. Por favor, inténtalo de nuevo en unos momentos.'
    );
  }
};